import {Card} from '@material-ui/core';
import {Page} from 'Core/models/Page';
import React, {ReactNode, useMemo} from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import {VariableSizeList as List} from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import CardField from 'Shared/components/CardField';
import 'Shared/components/PagedView/Card/CardBody.sass';
import {CardFilters} from 'Shared/components/PagedView/Card/CardFilterOverlay';
import {Column} from 'Shared/components/PagedView/PagedView';

type CardBodyProps<TData> = {
  data: Page<TData>;
  filters: Array<CardFilters<any>>;
  loadMore: (page: Page<TData>) => void;
  columns: Array<Column<TData>>;
};

const CardBody = <TData extends {}>(props: CardBodyProps<TData>) => {
  const {columns, data} = props;

  return (
    <>
      <div className="paged-card__content">
        <AutoSizer>
          {({height, width}) => (
            <InfiniteLoader
              isItemLoaded={index => !!props.data.result[index]}
              itemCount={props.data.totalCount}
              loadMoreItems={(start, stop) => {
                if (stop > props.data.result.length && start < props.data.totalCount) {
                  props.loadMore(props.data);
                }

                return null;
              }}
            >
              {({onItemsRendered, ref}) => (
                <List
                  onItemsRendered={onItemsRendered}
                  ref={ref}
                  height={height}
                  width={width}
                  itemCount={props.data.result.length}
                  itemSize={() => columns.length * 31 + 58}
                >
                  {({index, style}) => (
                    <div style={style}>
                      <Card className="paged-card">
                        {columns.map(col => (
                          <div key={`${col.title}_${index}`}>
                            <TableCell title={col.title} renderColumn={col.getContent} index={index} data={data} />
                          </div>
                        ))}
                      </Card>
                    </div>
                  )}
                </List>
              )}
            </InfiniteLoader>
          )}
        </AutoSizer>
      </div>
    </>
  );
};

const TableCell = React.memo(
  <TData extends {}>(props: {
    renderColumn: (data: any) => ReactNode;
    index: number;
    data: Page<TData>;
    title: string;
  }) => {
    const {renderColumn, data, index, title} = props;
    const {result} = data;

    const content = useMemo(() => renderColumn(result[index]), [index, renderColumn, result]);

    return title ? <CardField className="card-body__field" name={title} value={content} /> : <>{content}</>;
  }
);

export default CardBody;
